'use client';

import { useCallback } from 'react';
import Image from '@/components/Image';
import Button from '@/components/Button';
import { CouponSectionBlock } from '../types';
import { useValidExposure } from '@/hooks/useValidExposure';

interface CouponSectionProps {
  isWeb: boolean;
  content: CouponSectionBlock;
  index: number;
  trackADExp: (
    moduleName: string,
    pitsIndex: number,
    pitsJumpType?: string,
    pitsJumpContent?: string,
  ) => void;
  trackADClick: (
    moduleName: string,
    pitsIndex: number,
    pitsJumpType?: string,
    pitsJumpContent?: string,
  ) => void;
}

export function CouponSection({
  isWeb,
  content,
  index,
  trackADExp,
  trackADClick,
}: CouponSectionProps) {
  const {
    pc_image,
    mobile_image,
    pc_width,
    pc_height,
    mobile_width,
    mobile_height,
    image_alt,
    url,
    discount_text,
    price_text,
    coupon_code,
    coupon_valid_date,
    button_text,
    module_name,
  } = content;
  const image = isWeb ? pc_image : mobile_image;
  const width = isWeb ? pc_width : mobile_width;
  const height = isWeb ? pc_height : mobile_height;

  const { ref: validExposureRef } = useValidExposure({
    callback: useCallback(() => {
      trackADExp(module_name, index, url ? '4' : '0', url || undefined);
    }, [module_name, index, trackADExp, url]),
  });

  const copyCouponCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(coupon_code);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = coupon_code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }, [coupon_code]);

  const handleCopy = useCallback(() => {
    trackADClick(module_name, index, url ? '4' : '0', url || undefined);
    void copyCouponCode();

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [copyCouponCode, url, module_name, index, trackADClick]);

  if (isWeb) {
    return (
      <section
        ref={validExposureRef}
        className="relative w-full overflow-hidden bg-white flex lg:flex-row flex-col lg:mb-0 lg:mb-[96px] mb-[48px]"
      >
        <div className="flex-1 relative">
          <Image
            className="block lg:w-full w-[90%] mx-auto lg:h-full object-cover"
            src={image}
            alt={image_alt}
            width={width}
            height={height}
          />
        </div>
        <div className="flex-1 pl-[4.1667vw] flex flex-col lg:items-start items-center justify-center">
          <p
            className="2xl:text-[28px] xl:text-[24px] lg:text-[20px] text-[28px] text-[#000000] 2xl:leading-[34px] xl:leading-[28px] lg:leading-[24px] leading-[34px] font-bb mt-[12px]"
            dangerouslySetInnerHTML={{ __html: discount_text }}
          ></p>
          <p
            className="2xl:text-[64px] xl:text-[56px] text-[48px] text-[#000000] 2xl:leading-[77px] xl:leading-[67px] lg:leading-[57px] leading-[67px] font-bb"
            dangerouslySetInnerHTML={{ __html: price_text }}
          ></p>
          <div
            className="2xl:my-[48px] xl:my-[32px] lg:my-[16px] my-[48px] w-[333px] h-[130px] text-[#e0004d] text-center"
            style={{
              backgroundImage:
                'url(https://s3.zeelool.com/admin/shop/home/e6da664dc12981c7528353120e3f4704.png)',
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          >
            <p className="font-bb pt-[39px] pb-[10px] text-[32px] leading-[36px]">
              CODE: {coupon_code}
            </p>
            <p className="font-br text-[16px] leading-[19px]">{coupon_valid_date}</p>
          </div>
          <Button
            sx={{
              width: '333px',
              height: '60px',
              borderRadius: '8px',
              backgroundColor: '#E0004D',
              color: '#FFFFFF',
              fontFamily: 'var(--font-bricolage-bold)',
              fontSize: '24px',
            }}
            onClick={handleCopy}
          >
            {button_text}
          </Button>
        </div>
      </section>
    );
  }
  return (
    <section ref={validExposureRef} className="relative w-full overflow-hidden bg-white mb-[57px]">
      <Image
        src={image}
        alt={image_alt}
        className="block w-full h-auto object-cover"
        width={width}
        height={height}
      />
      <p className="text-[14px] font-bb text-[#000000] leading-[17px] pb-[4px] leading-[38px] text-center pt-[11px]">
        {discount_text}
      </p>
      <p
        className="text-[32px] font-bb text-[#000000] text-center"
        dangerouslySetInnerHTML={{ __html: price_text }}
      ></p>
      <p className="text-[24px] font-bb text-[#e0004d] mt-[31px] pb-[10px] text-center">
        CODE: {coupon_code}
      </p>
      <p className="text-[14px] font-br text-[#808080] text-center mt-[4px]">{coupon_valid_date}</p>
      <Button
        sx={{
          width: '220px',
          height: '40px',
          borderRadius: '4px',
          backgroundColor: '#E0004D',
          color: '#FFFFFF',
          fontFamily: 'var(--font-bricolage-bold)',
          fontSize: '14px',
          marginTop: '20px',
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'block',
        }}
        onClick={handleCopy}
      >
        {button_text}
      </Button>
    </section>
  );
}
